/* eslint-disable */
import { create } from 'zustand';
import { requestNavigation } from './navGuard';

// T-1001 Strategy α+δ — default Developments sub-view on cold entry / portal switch.
// Replaces the prior localStorage hydration: per Convention #36,
// URL params restore on refresh (intentional), but portal-switch and cold-login
// must land on a clean default. Consumers in AppHeader + App.js cold-entry path
// import this and pass it to setDevView() before setTab().
export const DEFAULT_DEV_VIEW = 'board';

// ── Analytics sub-view URL sync (Phase 2) ────────────────────────────────────
// View lives in URL (?analytics_view=...); filters live in component state
// (see src/components/developments/analytics/context/AnalyticsFiltersContext.js).
// Refresh preserves view; share-URL lands recipient on same view with empty filters.
const ANALYTICS_VIEWS = ['overview', 'flow', 'portfolio', 'people', 'trends'];

function getAnalyticsViewFromURL() {
  try {
    if (typeof window === 'undefined') return null;
    const v = new URLSearchParams(window.location.search).get('analytics_view');
    return ANALYTICS_VIEWS.includes(v) ? v : null;
  } catch (e) { return null; }
}

function syncAnalyticsViewToURL(view) {
  try {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (view === 'overview' || !view) url.searchParams.delete('analytics_view');
    else                              url.searchParams.set('analytics_view', view);
    // replaceState so view-switching doesn't pollute browser back history
    window.history.replaceState({}, '', url);
  } catch (e) { /* no-op */ }
}

// ── Settings sub-view + sub-panel URL sync (Phase 1) ────────────────────────
// Mirrors analyticsView pattern exactly. Two URL params work together:
//   ?settings_view=<domain>   — one of SETTINGS_VIEWS (absent means 'hub')
//   ?settings_panel=<panel>   — optional sub-panel inside a drill-down
// Refresh preserves both. Leaving the hub clears the params.
// 'hub' is the canonical default; any unknown value falls back to 'hub'.
const SETTINGS_VIEWS = ['hub', 'hygiene', 'schema', 'lifecycle', 'imports', 'system', 'activity'];

function getSettingsViewFromURL() {
  try {
    if (typeof window === 'undefined') return null;
    const v = new URLSearchParams(window.location.search).get('settings_view');
    return SETTINGS_VIEWS.includes(v) ? v : null;
  } catch (e) { return null; }
}

function getSettingsPanelFromURL() {
  try {
    if (typeof window === 'undefined') return null;
    const p = new URLSearchParams(window.location.search).get('settings_panel');
    return p || null;
  } catch (e) { return null; }
}

function syncSettingsToURL(view, panel) {
  try {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (view === 'hub' || !view) {
      url.searchParams.delete('settings_view');
      url.searchParams.delete('settings_panel');
    } else {
      url.searchParams.set('settings_view', view);
      if (panel) url.searchParams.set('settings_panel', panel);
      else       url.searchParams.delete('settings_panel');
    }
    // replaceState so view-switching doesn't pollute browser back history
    window.history.replaceState({}, '', url);
  } catch (e) { /* no-op */ }
}

// ── Sales Analytics v2 sub-view + model deep-link URL sync (mirrors settingsView) ──
//   ?sales_analytics=<view>   — breakdown (default, param absent) / models / canvas / dashboards
//   &models=CL9218,CL3857     — Model Explorer deep-link (≤4), cleared when empty
const SALES_ANALYTICS_VIEWS = ['breakdown', 'models', 'canvas', 'command', 'collection', 'production', 'stock'];

function getSalesAnalyticsViewFromURL() {
  try {
    if (typeof window === 'undefined') return null;
    const v = new URLSearchParams(window.location.search).get('sales_analytics');
    return SALES_ANALYTICS_VIEWS.includes(v) ? v : null;
  } catch (e) { return null; }
}

function getSalesModelsFromURL() {
  try {
    if (typeof window === 'undefined') return [];
    const m = new URLSearchParams(window.location.search).get('models');
    return m ? m.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 4) : [];
  } catch (e) { return []; }
}

function syncSalesAnalyticsToURL(view, models) {
  try {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (view === 'breakdown' || !view) url.searchParams.delete('sales_analytics');
    else                               url.searchParams.set('sales_analytics', view);
    if (models && models.length) url.searchParams.set('models', models.join(','));
    else                         url.searchParams.delete('models');
    window.history.replaceState({}, '', url);
  } catch (e) { /* no-op */ }
}

// ── Sales department (lifted from SalesPortal local state, Sales redesign) ────
// Department now lives here so the AppHeader Sales nav-dropdown can set it while
// SalesPortal consumes it. Preserves the legacy localStorage key + the
// 'Optic Eyewear' → 'Optic' migration that lived in SalesPortal.js:124-129.
const SALES_DEPT_KEY = 'salesPortalDepartment';
function getSalesDepartmentFromStorage() {
  try {
    if (typeof window === 'undefined') return 'Sunglasses';
    const saved = localStorage.getItem(SALES_DEPT_KEY);
    if (saved === 'Optic Eyewear') { localStorage.setItem(SALES_DEPT_KEY, 'Optic'); return 'Optic'; }
    return saved || 'Sunglasses';
  } catch (e) { return 'Sunglasses'; }
}

export const useUiStore = create((set) => ({
  // Portal / navigation
  tab: 'catalog',
  entryPhase: 'selection', // 'selection' | 'splash' | 'app' (Brief 3 removed 'transition')
  transitionPortal: null,

  // Dev UI — T-1001 Strategy δ: localStorage backing dropped.
  // Cold login + portal switch land on DEFAULT_DEV_VIEW; refresh inside Developments
  // also lands here (URL params drive deeper sub-views per Convention #36).
  devView: DEFAULT_DEV_VIEW,

  // Analytics sub-view (inside Developments → Analytics)
  analyticsView: getAnalyticsViewFromURL() || 'overview',

  // Settings sub-view + sub-panel (inside Developments → Settings)
  settingsView:  getSettingsViewFromURL()  || 'hub',
  settingsPanel: getSettingsPanelFromURL() || null,

  // Sales Analytics v2 sub-view (breakdown default) + Model Explorer deep-link
  salesAnalyticsView:   getSalesAnalyticsViewFromURL() || 'breakdown',
  salesAnalyticsModels: getSalesModelsFromURL(),

  // Dev-number cross-portal search — cue for Archive view to auto-open
  // its drawer on a specific dev. Set by DevNumberResultModal when the
  // user clicks "Open in Archive"; consumed + cleared by CollectionDetail
  // once the target dev is rendered.
  archiveDrawerDevId: null,

  // Settings · Activity date range — Phase 4.
  // preset: '7d' | '30d' | '90d' | 'all' | null (null = custom range)
  // from / to: ISO date strings when preset is null. Default = 30d.
  activityRange: { preset: '30d', from: null, to: null },
  devSearch: '',
  devSort: 'newest',
  devFilters: { status: new Set(), designer: new Set(), type: new Set(), material: new Set(), shape: new Set(), season: new Set(), factory: new Set(), sunOptic: new Set(), decision: new Set(), procurement: new Set() },
  showDevForm: false,
  devFormPrefill: null,
  devFormRequestLink: null,
  filteredDevsList: [],
  // Same as filteredDevsList but with the externalFilters.status branch
  // skipped — used by DevStatusBar to compute pill counts via the
  // "ignoring this pill" trick (so e.g. the Drawing pill shows what the
  // user would get if they switched their stage selection).
  filteredIgnoringStatusList: [],

  // Catalog UI
  viewMode: 'grid',
  selectedItems: new Set(),
  filterPanelOpen: false,

  // Brief F — Catalog hamburger drawer (greenfield system-level surface).
  // Phase 1 ships drawer + temp dev trigger in isolation; Phase 2 wires
  // the real hamburger button in AppHeader and removes the temp trigger.
  // `expandedDrawerPortal` is the portal id whose subsections are revealed
  // (`catalog | developments | qc | sales`); null means all collapsed.
  showHamburgerDrawer: false,
  expandedDrawerPortal: null,

  // T-015 (lifted in Brief F Phase 2) — system-level Activity drawer open
  // state. Previously local to AppHeader; lifted so CatalogDrawer's
  // Workspace → Activity row can trigger it without prop drilling, and
  // so both surfaces share a single source of truth.
  showActivityDrawer: false,

  // Sales department (Sunglasses | Optic | All) — Sales redesign Commit 1.
  salesDepartment: getSalesDepartmentFromStorage(),

  // Sales view (items | models | analytics | history | data) — Sales fix pass
  // FIX 1: the legacy sub-tabs row is gone; the Sales nav-dropdown Sections drive
  // this. Default 'items' on cold entry / portal switch.
  salesView: 'items',
  // Collection Gate scope — shared by ALL sales surfaces (one mental switch). Session-scoped.
  salesScope: 'current', // 'current' | 'all'

  // Brief E.2 Phase 1 — portal-nav dropdown state. null = all closed.
  // Click on a portal-nav item with subsections opens its dropdown;
  // 4 close affordances are wired in AppHeader: click outside / ESC /
  // click another portal / click a sub-section item.
  openPortalDropdown: null,

  // Global search
  globalSearch: '',
  showGlobalSearch: false,

  // Modals & panels
  showExport: false,
  showImport: false,
  showBrandManager: false,
  showActionsMenu: false,
  fullScreen: null,
  lightboxSrc: null,
  hoverPreview: null,
  devRequestItem: null,
  devReqNotification: null,

  // Detail/gallery state
  detailImgIdx: 0,
  fsImgIdx: 0,
  fsZoomed: false,

  // Shortcuts
  showShortcuts: false,

  // Toast
  toast: null,

  // ── Actions ──
  setTab: (tab) => { if (requestNavigation(() => set({ tab }))) return; set({ tab }); }, // navGuard.js may interpose
  setEntryPhase: (v) => set({ entryPhase: v }),
  setTransitionPortal: (v) => set({ transitionPortal: v }),
  setDevView: (v) => set({ devView: v }),
  setAnalyticsView: (v) => { set({ analyticsView: v }); syncAnalyticsViewToURL(v); },
  setSettingsView: (v, panel = null) => {
    set({ settingsView: v, settingsPanel: panel });
    syncSettingsToURL(v, panel);
  },
  setSettingsPanel: (p) => set((state) => {
    syncSettingsToURL(state.settingsView, p);
    return { settingsPanel: p };
  }),
  // Sales Analytics v2 — view + model deep-link, both mirrored to the URL
  setSalesAnalyticsView: (v) => set((state) => {
    syncSalesAnalyticsToURL(v, state.salesAnalyticsModels);
    return { salesAnalyticsView: v };
  }),
  setSalesAnalyticsModels: (arr) => set((state) => {
    const models = (arr || []).slice(0, 4);
    syncSalesAnalyticsToURL(state.salesAnalyticsView, models);
    return { salesAnalyticsModels: models };
  }),
  setArchiveDrawerDevId: (id) => set({ archiveDrawerDevId: id || null }),
  setActivityRange: (range) => set({ activityRange: range || { preset: '30d', from: null, to: null } }),
  setDevSearch: (v) => set({ devSearch: v }),
  setDevSort: (v) => set({ devSort: v }),
  setDevFilters: (v) => set(state => ({ devFilters: typeof v === 'function' ? v(state.devFilters) : v })),
  setShowDevForm: (v) => set({ showDevForm: v }),
  setDevFormPrefill: (v) => set({ devFormPrefill: v }),
  setDevFormRequestLink: (v) => set({ devFormRequestLink: v }),
  setFilteredDevsList: (v) => set(state => ({ filteredDevsList: typeof v === 'function' ? v(state.filteredDevsList) : v })),
  setFilteredIgnoringStatusList: (v) => set(state => ({ filteredIgnoringStatusList: typeof v === 'function' ? v(state.filteredIgnoringStatusList) : v })),
  setViewMode: (v) => set({ viewMode: v }),
  setSelectedItems: (v) => set(state => ({ selectedItems: typeof v === 'function' ? v(state.selectedItems) : v })),
  setFilterPanelOpen: (v) => set(state => ({ filterPanelOpen: typeof v === 'function' ? v(state.filterPanelOpen) : v })),
  setShowHamburgerDrawer: (v) => set(state => ({ showHamburgerDrawer: typeof v === 'function' ? v(state.showHamburgerDrawer) : v })),
  setExpandedDrawerPortal: (v) => set(state => ({ expandedDrawerPortal: typeof v === 'function' ? v(state.expandedDrawerPortal) : v })),
  toggleDrawerPortal: (portalId) => set(state => ({ expandedDrawerPortal: state.expandedDrawerPortal === portalId ? null : portalId })),
  toggleHamburgerDrawer: () => set(state => ({ showHamburgerDrawer: !state.showHamburgerDrawer })),
  setShowActivityDrawer: (v) => set(state => ({ showActivityDrawer: typeof v === 'function' ? v(state.showActivityDrawer) : v })),
  toggleActivityDrawer: () => set(state => ({ showActivityDrawer: !state.showActivityDrawer })),
  setOpenPortalDropdown: (key) => set({ openPortalDropdown: key }),
  setSalesDepartment: (d) => {
    try { if (typeof window !== 'undefined') localStorage.setItem(SALES_DEPT_KEY, d); } catch (e) { /* no-op */ }
    set({ salesDepartment: d });
  },
  setSalesView: (v) => set({ salesView: v }),
  setSalesScope: (v) => set({ salesScope: v === 'all' ? 'all' : 'current' }),
  setGlobalSearch: (v) => set({ globalSearch: v }),
  setShowGlobalSearch: (v) => set({ showGlobalSearch: v }),
  setShowExport: (v) => set({ showExport: v }),
  setShowImport: (v) => set({ showImport: v }),
  setShowBrandManager: (v) => set({ showBrandManager: v }),
  setShowActionsMenu: (v) => set({ showActionsMenu: v }),
  setFullScreen: (v) => set({ fullScreen: v }),
  setLightboxSrc: (v) => set({ lightboxSrc: v }),
  setHoverPreview: (v) => set({ hoverPreview: v }),
  setDevRequestItem: (v) => set({ devRequestItem: v }),
  setDevReqNotification: (v) => set({ devReqNotification: v }),
  setDetailImgIdx: (v) => set({ detailImgIdx: v }),
  setFsImgIdx: (v) => set({ fsImgIdx: v }),
  setFsZoomed: (v) => set({ fsZoomed: v }),
  setShowShortcuts: (v) => set({ showShortcuts: v }),
  showToast: (msg, err = false) => {
    set({ toast: { msg, err } });
    setTimeout(() => set({ toast: null }), 2800);
  },
}));
